import { Filter, getStaticFilter } from '@/api/models';
import { EntityDrawerView } from '@/components/EntityDrawerView';
import { FilterFormData } from '@/pages/filters/hooks/useFilterMutations';
import * as React from 'react';
import { FilterForm } from './FilterForm';

export interface FilterDrawerViewProps {
  isOpen: boolean;
  filter: Filter | null;
  mode: 'view' | 'create' | 'edit';
  isLoading: boolean;
  isFormLoading: boolean;
  onClose: () => void;
  onEdit: (filter: Filter) => void;
  onDelete: (filter: Filter) => void;
  onFormSubmit: (data: FilterFormData) => Promise<void>;
}

export const FilterDrawerView: React.FC<FilterDrawerViewProps> = ({
  isOpen,
  filter,
  mode,
  isLoading,
  isFormLoading,
  onClose,
  onEdit,
  onDelete,
  onFormSubmit,
}) => (
  <EntityDrawerView<Filter, FilterFormData>
    isOpen={isOpen}
    entity={filter}
    mode={mode}
    isLoading={isLoading}
    isFormLoading={isFormLoading}
    entityLabel='Filter'
    spinnerColor='border-coffice-orange'
    onClose={onClose}
    onEdit={onEdit}
    onDelete={onDelete}
    onFormSubmit={onFormSubmit}
    getTitle={(entity, m) => {
      const staticFilter = entity ? getStaticFilter(entity.name) : null;
      switch (m) {
        case 'create':
          return 'Create New Filter';
        case 'edit':
          return entity
            ? `Edit ${staticFilter?.label || entity.name}`
            : 'Edit Filter';
        default:
          return entity ? staticFilter?.label || entity.name : 'Filter Details';
      }
    }}
    renderHeader={entity => {
      const staticFilter = getStaticFilter(entity.name);
      return (
        <div className='flex items-center space-x-4 pb-6 border-b border-grey-200'>
          <div className='w-16 h-16 bg-grey-100 rounded-full flex items-center justify-center text-4xl'>
            {staticFilter?.emoji || '🏷️'}
          </div>
          <div className='flex-1'>
            <h3 className='text-xl font-semibold text-grey-900'>
              {staticFilter?.label || entity.name}
            </h3>
            <div className='text-sm text-grey-500'>{entity.name}</div>
            <div className='mt-2 flex items-center space-x-2'>
              {entity.enabled ? (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
                  Enabled
                </span>
              ) : (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
                  Disabled
                </span>
              )}
              {entity.isMandatory && (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700'>
                  Mandatory
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }}
    renderDetails={entity => (
      <>
        <div>
          <dt className='text-xs text-grey-500'>Businesses Using</dt>
          <dd className='text-sm text-grey-900'>
            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-700'>
              {entity.businessCount ?? 0}
            </span>
          </dd>
        </div>
        <div>
          <dt className='text-xs text-grey-500'>Mandatory</dt>
          <dd className='text-sm text-grey-900'>
            {entity.isMandatory ? (
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700'>
                Yes
              </span>
            ) : (
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-700'>
                No
              </span>
            )}
          </dd>
        </div>
      </>
    )}
    renderForm={props => (
      <FilterForm
        filter={props.entity}
        onSubmit={props.onSubmit}
        onCancel={props.onCancel}
        loading={props.loading}
      />
    )}
    getId={entity => entity.id!}
    getCreatedAt={entity => entity.createdAt}
    getUpdatedAt={entity => entity.updatedAt}
  />
);
